import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getI18n, useTranslation } from "react-i18next";
import { Model, surveyLocalization } from "survey-core";
import "survey-core/defaultV2.min.css";
import "survey-core/survey.i18n";
import { Survey } from "survey-react-ui";

import { initSurvey } from "../../onboarding/components/surveyjs/survey/init-survey";
import { mapSurveyLanguage } from "../../onboarding/network/network";
import "./kash-survey.css";

interface Props {
  json: any;
  onSubmit: (data: any) => Promise<any>;
  defaultValue?: any;
  disabled?: boolean;
  onSurveyCreated?: (model: Model) => void;
  language?: string; // Optional language override from parent
}

/**
 * KASH Survey Component
 *
 * A simplified survey component specifically designed for KASH assessments.
 * Features:
 * - Multiple questions per page
 * - Radio group questions with preset="radio"
 * - No complex form validation or section management
 * - Focus on straightforward survey completion
 * - Support for language override via language prop
 */
export const KashSurveyComponent: React.FC<Props> = ({
  onSubmit,
  json,
  defaultValue,
  disabled = false,
  onSurveyCreated,
  language,
}) => {
  const { t } = useTranslation();
  initSurvey();

  const [defaultFormVal, setDefaultFormVal] = useState(defaultValue || {});
  const [submitting, setSubmitting] = useState(false);
  const [currentPageNo, setCurrentPageNo] = useState(0);
  const [isCurrentPageValid, setIsCurrentPageValid] = useState(false);
  const dataRef = useRef<any>({});

  // Initialize survey model
  const survey = useMemo(() => {
    const model = new Model(json);
    // Use language prop if provided, otherwise fallback to app language
    const appLang = language || getI18n().language;
    const surveyLang = mapSurveyLanguage(appLang);

    model.locale = surveyLang;
    surveyLocalization.defaultLocale = surveyLang;
    // Hide survey title and page titles
    model.title = "";
    // SurveyJS uses showTitle to control the survey title rendering
    // and showPageTitles to control individual page titles
    // Disable both to avoid displaying any default titles
    // @ts-ignore - some SurveyJS typings may not include showTitle
    model.showTitle = false;
    model.showPageTitles = false;
    model.showNavigationButtons = "none";
    model.questionErrorLocation = "bottom";
    model.showCompletedPage = false;
    model.questionsOnPageMode = "standard"; // Multiple questions per page
    model.showQuestionNumbers = "off";

    // Progress bar options - disable default and use custom
    model.showProgressBar = "off"; // Disable default progress bar

    // Set readonly mode if disabled
    if (disabled) {
      model.mode = "display"; // Set survey to display mode for readonly
    }

    // Set all radiogroup questions to use "radio" preset and handle readonly
    model.getAllQuestions().forEach((question: any) => {
      if (question.getType() === "radiogroup") {
        question.preset = "radio";
        question.isRequired = !disabled; // Don't require answers in readonly mode
        question.showClearButton = false;
      }

      // Set readonly for individual questions if disabled
      if (disabled) {
        question.readOnly = true;
      }
    });

    return model;
  }, [json, disabled, language]);

  // Notify parent component of survey model creation
  useEffect(() => {
    if (survey && onSurveyCreated) {
      onSurveyCreated(survey);
    }
  }, [survey, onSurveyCreated]);

  // Synchronize default values
  useEffect(() => {
    if (defaultFormVal && survey) {
      survey.mergeData(defaultFormVal);
    }
    setDefaultFormVal(defaultValue);
  }, [defaultValue, survey, defaultFormVal]);

  // Track current page data
  useEffect(() => {
    if (!survey.currentPage) return;

    const currentPageQuestions = survey.currentPage.questions;
    const currentPageQuestionsValue = {} as any;

    currentPageQuestions.forEach((question: any) => {
      currentPageQuestionsValue[question.name] = survey.data[question.name];
    });

    dataRef.current = {
      ...dataRef.current,
      ...currentPageQuestionsValue,
    };
  }, [survey, currentPageNo]);

  // Navigation handlers
  const goToNextPage = useCallback(async () => {
    if (submitting || disabled) return;

    const isValid = survey.validateCurrentPage();
    if (!isValid) return;

    if (survey.isLastPage) {
      // Submit the survey
      try {
        setSubmitting(true);
        await onSubmit(survey.data);
      } catch (error) {
        console.error("Survey submission failed:", error);
      } finally {
        setSubmitting(false);
      }
    } else {
      // Go to next page
      survey.nextPage();
      setCurrentPageNo(survey.currentPageNo);
    }
  }, [survey, onSubmit, submitting, disabled]);

  const goToPreviousPage = useCallback(() => {
    survey.prevPage();
    setCurrentPageNo(survey.currentPageNo);
  }, [survey, submitting, disabled]);

  // Function to check if current page is valid
  const checkCurrentPageValid = useCallback(() => {
    if (!survey?.currentPage) return false;

    return survey.currentPage.questions.every((question: any) => {
      return !question.isRequired || question.value !== undefined;
    });
  }, [survey]);

  // Survey event handlers
  useEffect(() => {
    if (!survey) return;

    survey.onValueChanged.add((_, options) => {
      dataRef.current = {
        ...dataRef.current,
        [options.name]: options.value,
      };
      // Update validation state when value changes
      setIsCurrentPageValid(checkCurrentPageValid());
    });

    survey.onCurrentPageChanged.add(() => {
      setCurrentPageNo(survey.currentPageNo);
      // Update validation state when page changes
      setIsCurrentPageValid(checkCurrentPageValid());
    });

    return () => {
      survey.onValueChanged?.clear?.();
      survey.onCurrentPageChanged?.clear?.();
    };
  }, [survey, checkCurrentPageValid]);

  // Get page progress info
  const pageProgress = useMemo(() => {
    if (!survey) return { current: 0, total: 0 };

    return {
      current: survey.currentPageNo + 1,
      total: survey.pageCount,
    };
  }, [survey, currentPageNo]);

  // Update validation state when page changes or values change
  useEffect(() => {
    setIsCurrentPageValid(checkCurrentPageValid());
  }, [survey, currentPageNo, checkCurrentPageValid]);

  return (
    <div className="pb-4">
      {/* Custom Progress Indicator */}
      <div className="m-4">
        <div className="mb-2 w-full">
          <span className="text-sm font-medium text-gray-700">
            {t("Common.page")} {pageProgress.current} / {pageProgress.total}
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className="bg-red-600 h-2 rounded-full transition-all duration-300 ease-in-out"
            style={{ width: `${(pageProgress.current / pageProgress.total) * 100}%` }}
          />
        </div>
      </div>

      {/* Survey content */}
      {/* @ts-ignore - SurveyJS types compatibility issue */}
      <Survey
        model={survey}
        className="survey-common"
        id="kash-assessment-survey"
        currentPageNo={currentPageNo}
      />

      {/* Navigation buttons */}
      {!disabled && (
        <div className="survey-navigation flex flex-row gap-3 m-4">
          <button
            type="button"
            className="nav-button flex-1 border-2 border-gray-200 bg-white text-gray-700 font-semibold py-3 rounded-lg hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-opacity-75 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={goToPreviousPage}
            disabled={currentPageNo === 0 || submitting}
          >
            {t("Common.previous")}
          </button>

          <button
            type="button"
            onClick={goToNextPage}
            disabled={!isCurrentPageValid || submitting}
            className="nav-button flex-1 bg-red-600 text-white font-semibold py-3 rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-400 focus:ring-opacity-75 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? (
              <>
                <span className="loading-spinner inline-block mr-2" />
                {t("Incentive.submitting_assessment")}
              </>
            ) : survey.isLastPage ? (
              t("Incentive.submit_assessment")
            ) : (
              t("Common.next")
            )}
          </button>
        </div>
      )}

      {/* Readonly mode navigation */}
      {disabled && (
        <div className="survey-navigation flex flex-row gap-3 m-4">
          <button
            type="button"
            className="nav-button flex-1 border-2 border-gray-200 bg-white text-gray-700 font-semibold py-3 rounded-lg hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-opacity-75 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={goToPreviousPage}
            disabled={currentPageNo === 0}
          >
            {t("Common.previous")}
          </button>

          <button
            type="button"
            disabled={survey.isLastPage}
            onClick={() => {
              if (survey.isLastPage) {
                // In readonly mode, just navigate back or close
                window.history.back();
              } else {
                survey.nextPage();
                setCurrentPageNo(survey.currentPageNo);
              }
            }}
            className="nav-button flex-1 border-2 border-gray-200 bg-white text-gray-700 font-semibold py-3 rounded-lg hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-opacity-75 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {survey.isLastPage ? t("Common.close") : t("Common.next")}
          </button>
        </div>
      )}
    </div>
  );
};
